import { InfoIcon } from 'lucide-react';
import React, { SetStateAction, useMemo, useOptimistic } from 'react';

import { PraetorianSettingsContent } from '@/components/PraetorianSettingsContent';
import { Tooltip } from '@/components/Tooltip';
import { useUpdateFlags } from '@/hooks/useFlags';
import { useMy } from '@/hooks/useMy';
import { PraetorianSettingsSectionProps } from '@/sections/settings/types';
import { ChariotModuleFlags, FlagType } from '@/types';

/**
 * Praetorian Settings Section Component
 *
 * Displays advanced settings and features available only to Praetorian users:
 * - Feature flags management (chariot-graph, chariot-insights, etc.)
 * - Delete account functionality
 * - Praetorian branding with logo
 *
 * @example
 * ```tsx
 * <PraetorianSettingsSection
 *   customerEmail="user@example.com"
 *   isPraetorianUser={true}
 *   showDeleteAccount={true}
 *   onDeleteAccount={() => setDeleteModalOpen(true)}
 * />
 * ```
 *
 * @example Without delete account option
 * ```tsx
 * <PraetorianSettingsSection
 *   customerEmail="user@example.com"
 *   isPraetorianUser={true}
 *   showDeleteAccount={false}
 *   showFeatureFlags={true}
 * />
 * ```
 */
export const PraetorianSettingsSection: React.FC<
  PraetorianSettingsSectionProps
> = ({
  customerEmail,
  customerDisplayName: _customerDisplayName,
  isPraetorianUser,
  isLoadingFlags,
  showDeleteAccount = false,
  showFeatureFlags: _showFeatureFlags = true,
  onDeleteAccount,
  title = 'Praetorian Only Section',
  showTitle = true,
  className = '',
}) => {
  // Fetch feature flags for the customer
  const { data: flagsData, isLoading: isLoadingFlagsInternal } = useMy(
    {
      resource: 'flag',
    },
    { email: customerEmail }
  );

  const { mutateAsync: updateFlags } = useUpdateFlags({
    email: customerEmail,
  });

  const isLoading = isLoadingFlags ?? isLoadingFlagsInternal;

  /**
   * State management strategy using React 19's useOptimistic hook:
   * - serverFlags: Derived from API data (single source of truth)
   * - optimisticFlags: Managed by useOptimistic for instant UI updates
   *
   * Benefits of useOptimistic:
   * - Automatic rollback on errors (no manual state management)
   * - Simpler mental model (React handles state transitions)
   * - No manual reset logic needed (React syncs with serverFlags)
   * - Type-safe and battle-tested by React team
   *
   * This replaces the previous two-state pattern (serverFlags + localFlags)
   * which required manual synchronization and error handling.
   */

  // Derive server flags from API data - single source of truth
  const serverFlags = useMemo(
    () =>
      (flagsData || [])
        .map(flag => flag.name)
        .filter(name => ChariotModuleFlags.includes(name as FlagType)),
    [flagsData]
  );

  // Use React 19's useOptimistic for automatic optimistic updates and error rollback
  const [optimisticFlags, setOptimisticFlags] = useOptimistic(
    serverFlags,
    (_currentState, optimisticValue: string[]) => optimisticValue
  );

  // Wrapper to match Dispatch<SetStateAction<string[]>> type expected by child components
  const handleSetFlags = (value: SetStateAction<string[]>) => {
    if (typeof value === 'function') {
      setOptimisticFlags(value(optimisticFlags));
    } else {
      setOptimisticFlags(value);
    }
  };

  // Only show this section to Praetorian users
  if (!isPraetorianUser) {
    return null;
  }

  return (
    <div
      className={`border-chariot-stroke bg-chariot-background-primary rounded-lg border ${className}`}
    >
      <div className="p-6">
        {/* Section Header with Praetorian Logo */}
        {showTitle && (
          <div className="mb-4 flex items-center gap-2">
            <h3 className="flex items-center gap-2 text-xl font-bold">
              <img src="/icons/logo.png" alt="logo" className="size-5" />
              {title}
            </h3>
            <Tooltip title="Configure advanced features and settings for this account">
              <InfoIcon className="text-chariot-text-secondary size-4" />
            </Tooltip>
          </div>
        )}

        {/* Praetorian Settings Content */}
        <PraetorianSettingsContent
          customerEmail={customerEmail}
          isPraetorianUser={isPraetorianUser}
          isLoadingFlags={isLoading}
          flags={optimisticFlags}
          setFlags={handleSetFlags}
          updateFlags={updateFlags}
          prevFlags={serverFlags}
          showDeleteAccount={showDeleteAccount}
          showPlexTrac={false} // PlexTrac is now in its own section
          onDeleteAccount={onDeleteAccount}
          className="p-0" // Remove extra padding since we already have it from the outer div
        />
      </div>
    </div>
  );
};
